/**
 * /tenants/health - Tenant-health dashboard (OCP-83).
 *
 * Tile-row summary at the top + sortable / filterable table below.
 * Auto-refreshes every 30s (matches the backend cache TTL so the
 * operator's actual touch is one DB round-trip per cycle).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';

interface TenantHealth {
  tenant_id: string;
  subdomain: string;
  custom_domain: string | null;
  region: string;
  vps_server_id: string | null;
  status: 'green' | 'yellow' | 'red' | 'inactive';
  subscription_state: string;
  // OCP-444: self-host telemetry tenant - no container of ours; beacon-only health.
  is_self_host: boolean;
  signals: {
    container: { state: string; last_seen_at: string | null };
    telemetry: { state: string; last_seen_at: string | null };
    last_error: { state: string; last_error_at: string | null; last_error_message: string | null };
    /** OCP-643 - instance admin password state from the beacon (separate account from the portal). */
    onboarding?: { state: 'ok' | 'password_pending' | 'unknown'; pending_since: string | null; pending_hours: number | null; warning: boolean };
    /** OCP-630 - the minute HTTP probe. */
    reachability?: {
      state: 'green' | 'yellow' | 'red' | 'unknown';
      consecutive_failures: number;
      last_probe_at: string | null;
      last_ok_at: string | null;
      latency_ms: number | null;
      last_error: string | null;
      incident_since: string | null;
    };
  };
}

interface FleetSummary {
  total: number;
  green: number;
  yellow: number;
  red: number;
  inactive: number;
}

type StatusFilter = 'all' | 'green' | 'yellow' | 'red' | 'inactive';

const STATUS_ORDER: Record<TenantHealth['status'], number> = {
  red: 0,
  yellow: 1,
  green: 2,
  inactive: 3,
};

const STATUS_DOT: Record<TenantHealth['status'], string> = {
  red: 'bg-red-500',
  yellow: 'bg-amber-500',
  green: 'bg-emerald-500',
  inactive: 'bg-gray-300',
};

const STATUS_LABEL: Record<TenantHealth['status'], string> = {
  red: 'Red',
  yellow: 'Yellow',
  green: 'Green',
  inactive: 'Inactive',
};

const POLL_INTERVAL_MS = 30_000;

export function AdminTenantHealthPage() {
  const [items, setItems] = useState<TenantHealth[] | null>(null);
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await api<{ items: TenantHealth[]; summary: FleetSummary }>(
          '/v1/admin/tenants/health',
        );
        if (cancelled) return;
        setItems(r.items);
        setSummary(r.summary);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load tenant health.');
      }
    };
    void fetchOnce();
    const timer = setInterval(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const regions = useMemo(() => {
    const set = new Set<string>();
    for (const t of items ?? []) set.add(t.region);
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = search.trim().toLowerCase();
    return items
      .filter((t) => {
        if (statusFilter !== 'all' && t.status !== statusFilter) return false;
        if (regionFilter !== 'all' && t.region !== regionFilter) return false;
        if (s.length > 0) {
          const hay = `${t.subdomain} ${t.custom_domain ?? ''}`.toLowerCase();
          if (!hay.includes(s)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const o = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        if (o !== 0) return o;
        return a.subdomain.localeCompare(b.subdomain);
      });
  }, [items, statusFilter, regionFilter, search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tenant health</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Per-tenant green/yellow/red roll-up across container, telemetry
          + last-error signals. Auto-refreshes every 30 seconds.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <SummaryTile label="Total" value={summary.total} dot="bg-gray-400" />
          <SummaryTile label="Green" value={summary.green} dot="bg-emerald-500" />
          <SummaryTile label="Yellow" value={summary.yellow} dot="bg-amber-500" />
          <SummaryTile label="Red" value={summary.red} dot="bg-red-500" />
          <SummaryTile label="Inactive" value={summary.inactive} dot="bg-gray-300" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="ml-2 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="red">Red</option>
            <option value="yellow">Yellow</option>
            <option value="green">Green</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400">
          Region
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="ml-2 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
          >
            <option value="all">All</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by subdomain or custom domain…"
          className="flex-1 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm md:max-w-sm"
        />
      </div>

      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {items && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {items.length === 0 ? 'No tenants yet.' : 'No tenants match these filters.'}
        </div>
      )}

      {items && filtered.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-left">Subscription</th>
                <th className="px-3 py-2 text-left">Region</th>
                <th className="px-3 py-2 text-left">Container</th>
                <th className="px-3 py-2 text-left">Telemetry</th>
                <th className="px-3 py-2 text-left">Last error</th>
                <th className="px-3 py-2 text-left">Reachability</th>
                <th className="px-3 py-2 text-left">Onboarding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {filtered.map((t) => (
                <tr key={t.tenant_id} data-testid={`tenant-row-${t.tenant_id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span
                        data-testid={`tenant-status-${t.tenant_id}`}
                        className={`block h-2 w-2 rounded-full ${STATUS_DOT[t.status]}`}
                      />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {STATUS_LABEL[t.status]}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/tenants/${t.tenant_id}`}
                      className="text-brand-700 underline"
                    >
                      {t.subdomain}
                    </Link>
                    {t.is_self_host && (
                      <span className="ml-2 inline-flex items-center rounded bg-sky-50 dark:bg-sky-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-200">
                        self-host
                      </span>
                    )}
                    {t.custom_domain && (
                      <p className="text-xs text-gray-500">{t.custom_domain}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{t.subscription_state}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{t.region}</td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                    {t.is_self_host ? (
                      <span className="text-gray-400" title="No container of ours to poll">
                        n/a
                      </span>
                    ) : (
                      <>
                        {t.signals.container.state}
                        <p className="text-gray-500">
                          {t.signals.container.last_seen_at
                            ? formatRelative(t.signals.container.last_seen_at)
                            : 'never'}
                        </p>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                    {t.signals.telemetry.state}
                    <p className="text-gray-500">
                      {t.signals.telemetry.last_seen_at
                        ? formatRelative(t.signals.telemetry.last_seen_at)
                        : 'never'}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                    {t.signals.last_error.last_error_at
                      ? formatRelative(t.signals.last_error.last_error_at)
                      : '-'}
                    {t.signals.last_error.last_error_message && (
                      <p
                        className="mt-0.5 truncate text-gray-500"
                        title={t.signals.last_error.last_error_message}
                      >
                        {t.signals.last_error.last_error_message}
                      </p>
                    )}
                  </td>
                  {/* OCP-630 - the minute probe: green ok, yellow / red = consecutive failures, red also = open incident. */}
                  <td className="px-3 py-2 text-xs" data-testid={`reachability-${t.tenant_id}`}>
                    {!t.signals.reachability || t.signals.reachability.state === 'unknown' ? (
                      <span className="text-gray-400" title="Not probed (self-host, probe disabled, or no probe yet)">-</span>
                    ) : t.signals.reachability.state === 'green' ? (
                      <span className="text-emerald-700 dark:text-emerald-300" title={t.signals.reachability.last_probe_at ? `last probe ${t.signals.reachability.last_probe_at}` : ''}>
                        ok{t.signals.reachability.latency_ms != null ? ` ${t.signals.reachability.latency_ms} ms` : ''}
                      </span>
                    ) : (
                      <span
                        className={t.signals.reachability.state === 'red' ? 'font-medium text-red-700 dark:text-red-300' : 'font-medium text-amber-700 dark:text-amber-300'}
                        title={t.signals.reachability.last_error ?? ''}
                      >
                        {t.signals.reachability.consecutive_failures} failed{t.signals.reachability.incident_since ? ` · down since ${new Date(t.signals.reachability.incident_since).toISOString().slice(11, 16)} UTC` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`onboarding-${t.tenant_id}`}>
                    {!t.signals.onboarding || t.signals.onboarding.state === 'unknown' ? (
                      <span className="text-gray-400" title="No beacon carried admin_password_pending yet (core < 0.188.1)">-</span>
                    ) : t.signals.onboarding.state === 'ok' ? (
                      <span className="text-gray-500">password set</span>
                    ) : (
                      <span
                        className={t.signals.onboarding.warning ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-gray-700 dark:text-gray-300'}
                        title="The instance admin has not set the instance password - only the welcome link gets them in (separate account from the portal)"
                      >
                        password pending{t.signals.onboarding.pending_hours != null ? ` ${t.signals.onboarding.pending_hours} h` : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, dot }: { label: string; value: number; dot: string }) {
  return (
    <div
      data-testid={`tile-${label.toLowerCase()}`}
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
        <span className={`block h-2 w-2 rounded-full ${dot}`} />
        {label}
      </div>
      <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const ageMs = now - then;
  if (ageMs < 60_000) return `${Math.max(0, Math.round(ageMs / 1000))}s ago`;
  if (ageMs < 60 * 60_000) return `${Math.round(ageMs / 60_000)}m ago`;
  if (ageMs < 24 * 60 * 60_000) return `${Math.round(ageMs / (60 * 60_000))}h ago`;
  return `${Math.round(ageMs / (24 * 60 * 60_000))}d ago`;
}
